import React, { useCallback, useEffect, useMemo } from 'react'

import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import { SH4, Stack } from '@zet/design-system'
import _ from 'lodash'
import { Controller, UseFormReturn, useWatch } from 'react-hook-form'

import { useApiAction } from '../../api/useApiAction'
import SelectDropDown from '../common/selectDropDown'
import TextInput from '../common/textInput'
import Footer from '../footer/footer'

type Props = {
  calculateEMI: ({ amount, tenure }: { amount: number; tenure: number }) => void
  calculateEMISuccess: boolean
  calculateEMILoading: boolean
  form: UseFormReturn<
    {
      amount: undefined
      tenure: undefined
    },
    any,
    undefined
  >
}

const Form = (props: Props) => {
  const { isLoading, stateManagePost, data } = useApiAction()
  const { calculateEMI, calculateEMISuccess, calculateEMILoading, form } = props

  const emiScreenConfigKeys = data?.emiScreenConfiguration?.keys || {}

  const onSubmit = (data) => {
    const payload = { formData: [] }
    Object.keys(data).forEach((key) => {
      payload.formData.push({
        id: emiScreenConfigKeys?.[key],
        value: data?.[key],
      })
    })
    stateManagePost({ payload })
  }

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    trigger,
    getValues,
  } = form

  const [tenure, amount] = useWatch({ name: ['tenure', 'amount'], control })

  const tenureOptions = useMemo(() => {
    const loanAndTenure = data?.emiScreenConfiguration?.loanAndTenure || []
    const tenureOptions = loanAndTenure.map((item) => ({
      optionId: item.tenure,
      optionValue: `${item.tenure} months`,
    }))
    const currentTenure = getValues('tenure')
    if (!tenureOptions?.find?.((item) => item.optionId === currentTenure)) {
      setValue('tenure', tenureOptions[0]?.optionId)
    }
    return tenureOptions
  }, [data])

  const minAmount = data?.emiScreenConfiguration?.minLoan || 0
  const maxAmount = useMemo(() => {
    return data?.emiScreenConfiguration?.loanAndTenure?.find?.(
      (item) => item?.tenure === tenure
    )?.maxLoan
  }, [tenure])

  const f = () => {
    if (amount && tenure && isValid) {
      calculateEMI({ amount, tenure })
    }
  }

  const onChangeAmountOrTenure = useCallback(_.debounce(f, 500), [
    amount,
    tenure,
    isValid,
  ])

  useEffect(() => {
    const amount = getValues('amount')
    const tenure = getValues('tenure')
    if (amount != undefined && tenure != undefined) {
      trigger()
      onChangeAmountOrTenure()
    }
  }, [tenure, amount, isValid])

  return (
    <KeyBoardWrapper
      className="items flex-1 rounded-t-lg bg-white"
      BottomSheet={
        <Footer
          step={
            data?.metadata?.step
              ? {
                  current: data?.metadata?.step?.current,
                  total: data?.metadata?.step?.total,
                }
              : undefined
          }
          buttonProps={{
            text: 'Continue',
            onPress: handleSubmit(onSubmit),
            disabled:
              !isValid ||
              isLoading ||
              !calculateEMISuccess ||
              calculateEMILoading,
          }}
        />
      }
    >
      <Stack className="gap-4 p-4">
        <Stack>
          <Controller
            name={'amount'}
            control={control}
            rules={{
              required: true,
              validate: {
                minAmount: (value) => +value >= minAmount,
                maxAmount: (value) => +value <= maxAmount,
              },
            }}
            render={({
              field: { onChange, onBlur, value },
              fieldState: { error },
            }) => (
              <TextInput
                label={'Loan Amount'}
                placeholder={'Enter your loan amount'}
                keyboardType={'number-pad'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error && 'Please enter a valid amount'}
              />
            )}
          />
          <SH4 className="mt-1 text-gray-500">
            Max Loan Amount = ₹{maxAmount?.toLocaleString?.('en-IN') ?? '--'}{' '}
            for {tenure} months tenure
          </SH4>
          <SH4 className="text-gray-500">
            Min Loan Amount = ₹{minAmount?.toLocaleString?.('en-IN')}
          </SH4>
        </Stack>
        <Controller
          name={'tenure'}
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, onBlur, value } }) => {
            return (
              <SelectDropDown
                label="Loan Tenure"
                placeholder="Select Loan Tenure"
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                options={tenureOptions}
              />
            )
          }}
        />
      </Stack>
    </KeyBoardWrapper>
  )
}

export default Form
